import { FC } from 'react';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { dailyNavAsOfLabel } from 'common/constants/dailyNav';
import { DAILY_NAV_TOOLTIP_SLOT_PROPS } from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { MarketDataFreshnessProps } from './MarketDataFreshness.props';

const refreshSpin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const FINALIZED_AS_OF_JUSTIFICATION =
  'This day has been finalized. Public comps and secondary prices are read as they existed at the finalize time, so later market revisions cannot change numbers that were already signed off.';

const OPEN_AS_OF_JUSTIFICATION =
  'This day is not finalized. Shows when the page last refreshed; after the market closes it shows the close, since that is when the data was last fresh. Refresh re-queries the latest data.';

// The earlier of the page's last refresh and the session close: once the market
// has closed, refreshing again cannot make the data any fresher than the close.
const resolveOpenDayAsOf = (sessionClose?: string | null, refreshedAt?: number): string | null => {
  const refreshed = refreshedAt ? new Date(refreshedAt) : null;
  const close = sessionClose ? new Date(sessionClose) : null;
  if (refreshed && close && !Number.isNaN(close.getTime())) {
    return (refreshed < close ? refreshed : close).toISOString();
  }
  return refreshed?.toISOString() ?? sessionClose ?? null;
};

const MarketDataFreshness: FC<MarketDataFreshnessProps> = ({
  asOf,
  refreshedAt,
  isFinalized,
  isRefreshing,
  onRefresh,
}) => {
  const label = dailyNavAsOfLabel(isFinalized ? asOf : resolveOpenDayAsOf(asOf, refreshedAt));
  if (!label) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" gap={0.25}>
      <Tooltip
        arrow
        describeChild
        placement="bottom-start"
        slotProps={DAILY_NAV_TOOLTIP_SLOT_PROPS}
        title={
          <Typography variant="caption" data-testid="daily-nav-market-data-as-of-justification">
            {isFinalized ? FINALIZED_AS_OF_JUSTIFICATION : OPEN_AS_OF_JUSTIFICATION}
          </Typography>
        }>
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="daily-nav-market-data-as-of"
          sx={{ borderBottom: '1px dashed', borderColor: 'text.disabled', cursor: 'help' }}>
          {isFinalized ? 'Market data as of' : 'Last refreshed'} {label}
        </Typography>
      </Tooltip>
      {!isFinalized && (
        <Tooltip title="Refresh market data">
          <span>
            <IconButton
              aria-label="Refresh market data"
              data-testid="daily-nav-refresh-market-data"
              disabled={isRefreshing}
              onClick={onRefresh}
              size="small"
              sx={{ p: 0.25 }}>
              <RefreshOutlined
                sx={{
                  fontSize: 14,
                  animation: isRefreshing ? `${refreshSpin} 0.8s linear infinite` : undefined,
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export default MarketDataFreshness;
